import { projectSelector } from "@rflocal/shared";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { TextPromptItem, BoxPromptingData } from "@functions/rapid/schemas";
import { InferenceTypes } from "@functions/rapid/services/inference/stream/types";
import { APIRequestError } from "./errors";

export interface InferenceServerPrediction {
    x: number;
    y: number;
    width: number;
    height: number;
    confidence: number;
    class: string;
    class_id: number;
    detection_id: string;
}

export interface Prediction extends InferenceServerPrediction {
    thumbnailDataUrl?: string;
    fileId: string;
    frameId: string;
    frameSegment: number;
    draft?: boolean;
    masks?: { counts: string; size: [number, number] };
    type?: "polygon" | "mask" | "box";
}

export type FramePrediction = {
    frame_number: number;
    predictions: InferenceServerPrediction[];
    image_width: number;
    image_height: number;
};

export type StreamInferRequestBody = {
    prompts?: BoxPromptingData[] | TextPromptItem[];
    workflowId?: string;
    apiKey?: string;
    fps?: number;
    fileId: string;
    startTimeMilliseconds?: number;
    endTimeMilliseconds?: number;
    inferenceType: InferenceTypes;
};

export type StreamInferEvent = { event: string; data: any };

export type StreamInferAbortedData = {
    message: string;
    reason: string;
    fileId?: string;
    errorCounts: Record<string, number>;
    totalErrorCount: number;
    threshold: number;
    processedBeforeAbort: number;
    totalItems: number;
};

export type StreamInferEventHandlers = {
    onStarted?: (data: { inferenceType: InferenceTypes; maxConcurrency: number }) => void;
    onResult?: (data: { result: FramePrediction; id: string }) => void;
    onComplete?: (data: { maxConcurrency: number; totalProcessed: number }) => void;
    onError?: (data: { message: string }) => void;
    onAborted?: (data: StreamInferAbortedData) => void;
    onProgress?: (progress: number) => void;
};

interface EventSourceMessage {
    id: string;
    event: string;
    data: string; // JSON stringified object
}

// Track active streams to prevent duplicates
const activeStreams = new Map<string, Promise<void>>();

// Track connection states to prevent fetchEventSource reconnections
const connectionStates = new Map<string, { isConnected: boolean; controller: AbortController }>();

const getStreamKey = ({
    fileId,
    fps,
    inferenceType
}: Pick<StreamInferRequestBody, "fileId" | "fps" | "inferenceType">) =>
    `streamInfer-${fileId}-${inferenceType}-${fps || 30}`;

export const streamInfer = async (
    data: StreamInferRequestBody,
    handlers: StreamInferEventHandlers
): Promise<void> => {
    console.log("🔍 [DEBUG] streamInfer called with:", {
        fileId: data.fileId,
        inferenceType: data.inferenceType,
        hasWorkflowId: !!data.workflowId,
        workflowId: data.workflowId,
        hasApiKey: !!data.apiKey,
        fps: data.fps,
        hasPrompts: !!data.prompts,
        promptsLength: data.prompts?.length
    });

    const streamKey = getStreamKey(data);

    // If there's already an active stream for this file, return that promise
    if (activeStreams.has(streamKey)) {
        console.log("🔍 [DEBUG] streamInfer already has an active stream for this file", streamKey);
        return activeStreams.get(streamKey)!;
    }

    // If there's an active connection, abort it first
    const existingConnection = connectionStates.get(streamKey);
    if (existingConnection?.isConnected) {
        existingConnection.controller.abort();
        connectionStates.delete(streamKey);
    }

    // Create new stream promise
    const streamPromise = new Promise<void>((resolve, reject) => {
        let isComplete = false;

        // Create an AbortController for this specific stream
        const abortController = new AbortController();

        // Track this connection
        connectionStates.set(streamKey, { isConnected: true, controller: abortController });

        const url = `${projectSelector.rapidStreamApiUrl()}/api/infer`;
        // console.log("🔍 [DEBUG] Making fetch to:", url);
        // console.log("🔍 [DEBUG] Request body:", JSON.stringify(data, null, 2));

        fetchEventSource(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "text/event-stream",
                "Cache-Control": "no-cache",
                "Connection": "keep-alive"
            },
            body: JSON.stringify(data),
            credentials: "include", // Include cookies for CORS

            // Additional configuration to prevent reconnections
            openWhenHidden: true, // Keep the stream alive even when the tab is hidden

            // Use the AbortController signal to allow manual abortion
            signal: abortController.signal,

            // Custom fetch to prevent timeouts and reconnections
            fetch: (input, init) => {
                // Check if connection is still valid before making fetch call
                const currentState = connectionStates.get(streamKey);
                if (!currentState?.isConnected) {
                    throw new Error("Connection aborted");
                }

                return fetch(input, { ...init, keepalive: true, signal: abortController.signal });
            },

            onopen: async (response) => {
                // Check if this is a reconnection (stream already exists)
                if (
                    activeStreams.has(streamKey) &&
                    activeStreams.get(streamKey) !== streamPromise
                ) {
                    console.warn("🚨 [DEBUG] UNEXPECTED RECONNECTION detected for:", streamKey);
                }

                if (!response.ok) {
                    reject(new APIRequestError(response.statusText, response.status));
                    return;
                }
            },

            onmessage: (event: EventSourceMessage) => {
                const { event: eventName, data: dataString } = event;

                let parsedData;

                // Check for aborted stream
                if (!activeStreams.has(streamKey)) return;

                // console.log("🔍 [DEBUG] onmessage", eventName);

                try {
                    parsedData = JSON.parse(dataString);
                } catch (error) {
                    console.error("🔍 [DEBUG] Error parsing JSON:", error);
                    // Skip invalid JSON (like "[DONE]" messages)
                    return;
                }

                switch (eventName) {
                    case "inference-started":
                        handlers.onStarted?.(parsedData);
                        break;
                    case "inference-result":
                        handlers.onResult?.(parsedData);
                        break;
                    case "inference-complete":
                        handlers.onComplete?.(parsedData);
                        isComplete = true;
                        activeStreams.delete(streamKey); // Clean up the active stream
                        connectionStates.delete(streamKey); // Clean up connection state
                        resolve(); // Resolve the promise when inference is complete
                        break;
                    case "inference-error":
                        // Note: inference-error is a per-item error, not a fatal stream error.
                        // The stream continues until inference-complete or inference-aborted.
                        const errMessage = parsedData.message || "Inference failed";
                        handlers.onError?.({ message: errMessage });
                        break;
                    case "inference-aborted":
                        handlers.onAborted?.(parsedData);
                        isComplete = true; // Treat as complete to prevent reconnection attempts
                        activeStreams.delete(streamKey);
                        connectionStates.delete(streamKey);
                        resolve(); // Resolve (not reject) - aborted is a controlled termination
                        break;
                }
            },

            onerror: (event) => {
                if (!isComplete) {
                    activeStreams.delete(streamKey); // Clean up on error
                    connectionStates.delete(streamKey); // Clean up connection state
                    handlers.onError?.({ message: event.data || "Stream error occurred" });
                    reject(new Error("Stream failed"));
                }
            },

            onclose: () => {
                if (!isComplete) {
                    activeStreams.delete(streamKey); // Clean up on unexpected close
                    connectionStates.delete(streamKey); // Clean up connection state
                    // Stream closed unexpectedly before completion
                    reject(new Error("Stream closed unexpectedly"));
                }
            }
        }).catch((error) => {
            console.log("Stream Ended!!", error);
            activeStreams.delete(streamKey); // Clean up on catch
            connectionStates.delete(streamKey); // Clean up connection state
            if (!isComplete) {
                reject(error);
            }
        });
    });

    // Store the promise in activeStreams
    activeStreams.set(streamKey, streamPromise);

    return streamPromise;
};

/**
 * Abort an active stream inference by file ID and inference type
 */
export const abortStreamInfer = (
    fileId: string,
    inferenceType: InferenceTypes,
    fps: number = 30
): boolean => {
    const streamKey = getStreamKey({ fileId, inferenceType, fps });
    const existingConnection = connectionStates.get(streamKey);

    const isConnected = !!existingConnection?.isConnected;

    try {
        console.log("🔍 [DEBUG] Aborting stream for:", streamKey);
        existingConnection?.controller?.abort();
    } catch (error) {
        console.error("🔍 [DEBUG] Error aborting stream:", error);
    }

    connectionStates.delete(streamKey);
    activeStreams.delete(streamKey);

    return isConnected;
};

export type CaptionInferRequestBody = { fileId: string };
export async function postCaptionInference(
    body: CaptionInferRequestBody,
    abortController?: AbortController
): Promise<string[]> {
    const response = await fetch(`${projectSelector.rapidUrl()}/api/caption`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: abortController?.signal
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: "caption inference failed" }));
        throw new Error(error.error || "caption inference failed");
    }

    return await response.json();
}
